import React, { useCallback, useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { BellIcon, XIcon, CalendarIcon, BookOpenIcon, AlertCircleIcon, ClockIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { getTeacherTasks, Task } from '../services/taskService';
import { getActiveAlerts, Alert } from '../services/alertService';
import { subscribeLessons } from '../src/services/lessonsService';
import type { Lesson } from '../src/types/lesson';
interface NotificationCenterProps {
  darkMode?: boolean;
}
interface Notification {
  id: string;
  type: 'lesson' | 'task' | 'alert';
  icon: React.ReactNode;
  title: string;
  message: string;
  timestamp: Date;
  link?: string;
  urgency: number; // Higher = more urgent
}
export const NotificationCenter: React.FC<NotificationCenterProps> = ({
  darkMode = true
}) => {
  const {
    currentUser
  } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasUrgentLesson, setHasUrgentLesson] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  // --- NEW: Seen/read tracking and brief animation ---
  const [seenIds, setSeenIds] = useState<Set<string>>(new Set());
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const animationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Compute unread count from notifications not yet seen
  const unreadCount = notifications.filter(n => !seenIds.has(n.id)).length;
  // When notifications change, check for new unseen ones and trigger brief animation
  useEffect(() => {
    const hasNewUnseen = notifications.some(n => !seenIds.has(n.id));
    if (hasNewUnseen && hasUrgentLesson && !isOpen) {
      setShouldAnimate(true);
      // Auto-stop animation after 2 seconds (roughly 2 shakes)
      if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
      animationTimerRef.current = setTimeout(() => {
        setShouldAnimate(false);
      }, 2000);
    } else if (!hasNewUnseen) {
      setShouldAnimate(false);
    }
    return () => {
      if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
    };
  }, [notifications, seenIds, hasUrgentLesson, isOpen]);
  // Mark all current notifications as seen when panel opens
  const handleBellClick = () => {
    if (!isOpen) {
      // Opening the panel — mark all as seen
      setSeenIds(prev => {
        const next = new Set(prev);
        notifications.forEach(n => next.add(n.id));
        return next;
      });
      setShouldAnimate(false);
      if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
    }
    setIsOpen(!isOpen);
  };
  // Session-based Set to track which toast reminders have been fired
  const dismissedToastsRef = useRef<Set<string>>(new Set());
  // Store latest data sources for interval-based rebuilds
  const latestDataRef = useRef<{
    tasks: Task[];
    alerts: Alert[];
    lessons: Lesson[];
  }>({
    tasks: [],
    alerts: [],
    lessons: []
  });
  // Format relative time with fresh "now"
  const formatRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffMin = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    if (diffHours < -24) {
      return `${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''} ago`;
    } else if (diffHours < -1) {
      return `${Math.abs(diffHours)} hour${Math.abs(diffHours) !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 0) {
      return 'recently';
    } else if (diffMin < 60) {
      return `in ${diffMin} min${diffMin !== 1 ? 's' : ''}`;
    } else if (diffHours < 24) {
      return `in ${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
    } else {
      return `in ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    }
  };
  // Build complete notification list from all sources
  const buildNotifications = useCallback((tasks: Task[], alerts: Alert[], lessons: Lesson[]): Notification[] => {
    const now = new Date(); // Fresh "now" for accurate calculations
    const allNotifications: Notification[] = [];
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    // Track if any lesson is within 1 hour (for bell pulse)
    let urgent = false;
    // Process lessons
    lessons.forEach(lesson => {
      if (lesson.status === 'CANCELED') {
        // Cancelled lesson (last 7 days)
        const lessonStart = new Date(lesson.startAt);
        if (lessonStart > weekAgo) {
          const cancelDateParam = lessonStart.toISOString().split('T')[0];
          allNotifications.push({
            id: `lesson-cancelled-${lesson.id}`,
            type: 'lesson',
            icon: <CalendarIcon className="h-5 w-5 text-red-500" data-id="element-2759" />,
            title: 'Lesson Cancelled',
            message: `${lesson.title || 'Lesson'} with ${lesson.studentName || 'student'} was cancelled`,
            timestamp: lessonStart,
            link: `/teacher/calendar?date=${cancelDateParam}`,
            urgency: 50
          });
        }
        return;
      }
      const lessonStart = new Date(lesson.startAt);
      const hoursUntil = (lessonStart.getTime() - now.getTime()) / (1000 * 60 * 60);
      // Lesson starting soon (within 1 hour) — highest priority reminder
      if (hoursUntil > 0 && hoursUntil <= 1) {
        urgent = true;
        const minsUntil = Math.round(hoursUntil * 60);
        const dateParam = lessonStart.toISOString().split('T')[0];
        allNotifications.push({
          id: `lesson-starting-soon-${lesson.id}`,
          type: 'lesson',
          icon: <ClockIcon className="h-5 w-5 text-orange-500" data-id="element-2760" />,
          title: 'Lesson Starting Soon',
          message: `${lesson.title || 'Lesson'} with ${lesson.studentName || 'student'} in ${minsUntil} min${minsUntil !== 1 ? 's' : ''}`,
          timestamp: lessonStart,
          link: `/teacher/calendar?date=${dateParam}`,
          urgency: 95
        });
      }
      // Upcoming lesson (1–24 hours away)
      else if (hoursUntil > 1 && hoursUntil <= 24) {
        const dateParam2 = lessonStart.toISOString().split('T')[0];
        allNotifications.push({
          id: `lesson-upcoming-${lesson.id}`,
          type: 'lesson',
          icon: <CalendarIcon className="h-5 w-5 text-green-500" data-id="element-2761" />,
          title: 'Upcoming Lesson',
          message: `${lesson.title || 'Lesson'} with ${lesson.studentName || 'student'} ${formatRelativeTime(lessonStart)}`,
          timestamp: lessonStart,
          link: `/teacher/calendar?date=${dateParam2}`,
          urgency: hoursUntil < 2 ? 90 : 70
        });
      }
    });
    // Update urgent state for bell pulse
    setHasUrgentLesson(urgent);
    // Process tasks
    tasks.forEach((task: Task) => {
      if (!task.dueDate || task.status === 'completed') return;
      const dueDate = new Date(task.dueDate);
      const hoursUntilDue = (dueDate.getTime() - now.getTime()) / (1000 * 60 * 60);
      // Overdue task
      if (hoursUntilDue < 0) {
        allNotifications.push({
          id: `task-overdue-${task.id}`,
          type: 'task',
          icon: <BookOpenIcon className="h-5 w-5 text-red-500" data-id="element-2762" />,
          title: 'Task Overdue',
          message: `"${task.title}" for ${task.studentName || 'student'} was due ${formatRelativeTime(dueDate)}`,
          timestamp: dueDate,
          link: '/assigned-tasks',
          urgency: 100
        });
      }
      // Due soon (within 48 hours)
      else if (hoursUntilDue <= 48) {
        allNotifications.push({
          id: `task-due-${task.id}`,
          type: 'task',
          icon: <BookOpenIcon className="h-5 w-5 text-amber-500" data-id="element-2763" />,
          title: 'Task Due Soon',
          message: `"${task.title}" for ${task.studentName || 'student'} is due ${formatRelativeTime(dueDate)}`,
          timestamp: dueDate,
          link: '/assigned-tasks',
          urgency: 80
        });
      }
    });
    // Process alerts
    alerts.forEach((alert: Alert) => {
      const alertDate = alert.createdAt?.toDate?.() || new Date();
      allNotifications.push({
        id: `alert-${alert.id}`,
        type: 'alert',
        icon: <AlertCircleIcon className="h-5 w-5 text-deep-pink" data-id="element-2764" />,
        title: 'Student Alert',
        message: `${alert.studentName}: ${alert.reason}`,
        timestamp: alertDate,
        link: `/reports?student=${alert.studentId}`,
        urgency: 60
      });
    });
    // Sort by urgency (desc) then timestamp (desc)
    const sorted = allNotifications.sort((a, b) => {
      if (a.urgency !== b.urgency) {
        return b.urgency - a.urgency;
      }
      return b.timestamp.getTime() - a.timestamp.getTime();
    });
    // Limit to 10
    return sorted.slice(0, 10);
  }, []);
  // Fire toast reminders for lessons starting within 1 hour
  const fireToastReminders = useCallback((lessons: Lesson[]) => {
    const now = new Date();
    lessons.forEach(lesson => {
      if (lesson.status === 'CANCELED') return;
      const lessonStart = new Date(lesson.startAt);
      const hoursUntil = (lessonStart.getTime() - now.getTime()) / (1000 * 60 * 60);
      if (hoursUntil > 0 && hoursUntil <= 1) {
        const toastKey = `reminder-1h-${lesson.id}`;
        if (!dismissedToastsRef.current.has(toastKey)) {
          dismissedToastsRef.current.add(toastKey);
          const minsUntil = Math.round(hoursUntil * 60);
          toast(`🎵 ${lesson.title || 'Lesson'} with ${lesson.studentName || 'student'} in ${minsUntil} min${minsUntil !== 1 ? 's' : ''}`, {
            id: toastKey,
            duration: 6000,
            icon: '⏰',
            style: {
              borderLeft: '4px solid #f97316',
              padding: '12px 16px'
            }
          });
        }
      }
    });
  }, []);
  // Fetch notifications with proper cleanup
  useEffect(() => {
    if (!currentUser?.uid) {
      setNotifications([]);
      setLoading(false);
      return;
    }
    let isMounted = true;
    let unsubscribeLessons: (() => void) | undefined;
    let refreshInterval: ReturnType<typeof setInterval> | undefined;
    const fetchNotifications = async () => {
      try {
        setLoading(true);
        setError(null);
        const now = new Date();
        // Expanded range: +25h to fully cover the 24-hour reminder window
        const futureWindow = new Date(now.getTime() + 25 * 60 * 60 * 1000);
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        // Fetch tasks and alerts (one-time)
        const [tasks, alerts] = await Promise.all([getTeacherTasks(currentUser.uid), getActiveAlerts(currentUser.uid)]);
        if (!isMounted) return;
        // Store for interval rebuilds
        latestDataRef.current.tasks = tasks;
        latestDataRef.current.alerts = alerts;
        // Subscribe to lessons (real-time)
        unsubscribeLessons = subscribeLessons({
          teacherId: currentUser.uid,
          from: weekAgo.toISOString(),
          to: futureWindow.toISOString(),
          onData: fetchedLessons => {
            if (!isMounted) return;
            // Store latest lessons for interval rebuilds
            latestDataRef.current.lessons = fetchedLessons;
            // Rebuild complete notification list on each update
            const next = buildNotifications(tasks, alerts, fetchedLessons);
            setNotifications(next);
            setLoading(false);
            // Fire toast reminders for ≤1h lessons
            fireToastReminders(fetchedLessons);
          }
        });
        // 1-minute refresh interval to keep reminder times accurate
        refreshInterval = setInterval(() => {
          if (!isMounted) return;
          const {
            tasks: t,
            alerts: a,
            lessons: l
          } = latestDataRef.current;
          if (l.length > 0 || t.length > 0 || a.length > 0) {
            const next = buildNotifications(t, a, l);
            setNotifications(next);
            // Re-check toast reminders (handles lessons that just crossed the 1h threshold)
            fireToastReminders(l);
          }
        }, 60 * 1000); // Every 60 seconds
      } catch (err) {
        console.error('Error fetching notifications:', err);
        if (isMounted) {
          setError("Couldn't load notifications. Please try again.");
          setLoading(false);
        }
      }
    };
    fetchNotifications();
    // Cleanup function
    return () => {
      isMounted = false;
      if (unsubscribeLessons) {
        unsubscribeLessons();
      }
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [currentUser?.uid, buildNotifications, fireToastReminders]);
  // Handle notification click
  const handleNotificationClick = (notification: Notification) => {
    // Mark this notification as seen
    setSeenIds(prev => {
      const next = new Set(prev);
      next.add(notification.id);
      return next;
    });
    setShouldAnimate(false);
    if (notification.link) {
      navigate(notification.link);
    }
    setIsOpen(false);
  };
  // Close on ESC key (desktop only)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isMobile) {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isMobile]);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen && !isMobile) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isMobile]);
  const renderPanel = () => {
    if (isMobile) {
      // Mobile: Fixed bottom drawer with portal
      return createPortal(<>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setIsOpen(false)} data-id="element-2765" />

          {/* Drawer */}
          <div className="fixed bottom-0 left-0 right-0 z-[70] animate-slide-up" data-id="element-2766">
            <div className={`rounded-t-2xl shadow-2xl max-h-[70vh] overflow-hidden ${darkMode ? 'bg-navy-800' : 'bg-white'}`} onClick={e => e.stopPropagation()} data-id="element-2767">
              {/* Header */}
              <div className={`px-4 py-4 border-b ${darkMode ? 'border-navy-700' : 'border-gray-200'} flex justify-between items-center sticky top-0 ${darkMode ? 'bg-navy-800' : 'bg-white'} z-10`} data-id="element-2768">
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-2769">
                  Notifications
                </h3>
                <button onClick={() => setIsOpen(false)} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-100'}`} data-id="element-2770">
                  <XIcon className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-2771" />
                </button>
              </div>

              {/* Content */}
              <div className="overflow-y-auto max-h-[calc(70vh-60px)]" data-id="element-2772">
                {loading ? <div className="px-4 py-8 text-center" data-id="element-2773">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-deep-pink mx-auto" data-id="element-2774"></div>
                  </div> : error ? <div className="px-4 py-8 text-center" data-id="element-2775">
                    <p className={`text-sm ${darkMode ? 'text-red-400' : 'text-red-600'}`} data-id="element-2776">
                      {error}
                    </p>
                  </div> : notifications.length > 0 ? <div data-id="element-2777">
                    {notifications.map(notification => <div key={notification.id} onClick={() => handleNotificationClick(notification)} className={`px-4 py-4 border-b ${darkMode ? 'border-navy-700' : 'border-gray-200'} active:bg-navy-600 transition-colors cursor-pointer`} data-id="element-2778">
                        <div className="flex gap-3" data-id="element-2779">
                          <div className={`rounded-full p-2 flex-shrink-0 ${darkMode ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-2780">
                            {notification.icon}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-2781">
                            <div className="flex justify-between items-start gap-2" data-id="element-2782">
                              <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-2783">
                                {notification.title}
                              </p>
                              <span className={`text-xs whitespace-nowrap ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2784">
                                {formatRelativeTime(notification.timestamp)}
                              </span>
                            </div>
                            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-2785">
                              {notification.message}
                            </p>
                          </div>
                        </div>
                      </div>)}
                  </div> : <div className="px-4 py-12 text-center" data-id="element-2786">
                    <div className={`rounded-full p-4 mx-auto w-fit mb-3 ${darkMode ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-2787">
                      <BellIcon className="h-8 w-8 text-gray-400" data-id="element-2788" />
                    </div>
                    <p className={`text-base ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-2789">
                      No notifications yet
                    </p>
                  </div>}
              </div>
            </div>
          </div>
        </>, document.body);
    } else {
      // Desktop: Dropdown (not portaled, positioned relative to button)
      return <div ref={dropdownRef} className={`absolute right-0 mt-2 w-80 sm:w-96 rounded-lg shadow-lg py-1 z-50 ${darkMode ? 'bg-navy-800 border border-navy-700' : 'bg-white border border-gray-200'}`} data-id="element-2790">
          <div className={`px-4 py-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-2791">
            <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-2792">
              Notifications
            </h3>
          </div>

          <div className="max-h-96 overflow-y-auto" data-id="element-2793">
            {loading ? <div className="px-4 py-6 text-center" data-id="element-2794">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-deep-pink mx-auto" data-id="element-2795"></div>
              </div> : error ? <div className="px-4 py-6 text-center" data-id="element-2796">
                <p className={`text-sm ${darkMode ? 'text-red-400' : 'text-red-600'}`} data-id="element-2797">
                  {error}
                </p>
              </div> : notifications.length > 0 ? <div data-id="element-2798">
                {notifications.map(notification => <div key={notification.id} onClick={() => handleNotificationClick(notification)} className={`block px-4 py-3 cursor-pointer hover:${darkMode ? 'bg-navy-700' : 'bg-gray-50'} border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-2799">
                    <div className="flex" data-id="element-2800">
                      <div className={`rounded-full p-2 mr-3 ${darkMode ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-2801">
                        {notification.icon}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-2802">
                        <div className="flex justify-between items-start" data-id="element-2803">
                          <p className={`font-medium truncate ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-2804">
                            {notification.title}
                          </p>
                          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} ml-2 whitespace-nowrap`} data-id="element-2805">
                            {formatRelativeTime(notification.timestamp)}
                          </span>
                        </div>
                        <p className={`text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-2806">
                          {notification.message}
                        </p>
                      </div>
                    </div>
                  </div>)}
              </div> : <div className="px-4 py-6 text-center" data-id="element-2807">
                <div className={`rounded-full p-3 mx-auto w-fit mb-3 ${darkMode ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-2808">
                  <BellIcon className="h-6 w-6 text-gray-400" data-id="element-2809" />
                </div>
                <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-2810">
                  No notifications yet
                </p>
              </div>}
          </div>
        </div>;
    }
  };
  return <div className="relative" data-id="element-2811">
      <button onClick={handleBellClick} className="relative p-1 rounded-full focus:outline-none focus:ring-2 focus:ring-deep-pink" aria-label="Notifications" data-id="element-2812">
        <BellIcon className={`h-6 w-6 text-white ${shouldAnimate ? 'animate-bell-pulse' : ''}`} data-id="element-2813" />
        {unreadCount > 0 && <span className={`absolute -top-1 -right-1 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center ${shouldAnimate ? 'bg-orange-500 animate-pulse' : 'bg-deep-pink'}`} data-id="element-2814">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>}
      </button>

      {isOpen && renderPanel()}
    </div>;
};